import React, { useEffect, useLayoutEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { glomInloggningsmal } from '../utils/postLoginRedirect';
import HelpModal from "./HelpModal";
import { useToast } from './ui/ToastProvider';
import SyncBanner from './SyncBanner';
import IntegrationHealthBanner from './IntegrationHealthBanner';
import { isRecentModulesDisabled } from '../featureFlags';
import TenantSwitchModal from './TenantSwitchModal';
import useUnreadNotifications from '../shell/hooks/useUnreadNotifications';
import useInboxUnread from '../shell/hooks/useInboxUnread';
import useSystemHealthQuick from '../shell/hooks/useSystemHealthQuick';
import { HelpCircle } from 'lucide-react';
import ModuleAccessContext from '../auth/ModuleAccessContext';
import { MODULES } from '../data/modules';
import { ADMIN_CRUMBS } from '../modules/SystemAdmin/systemAdminTabs';
import NoAccessPage from './NoAccessPage';
import './HeaderBar.css';
import NotificationPanel from './NotificationPanel';
import ProjectChatWidget from './ProjectChatWidget';
import KanaiWidget from './KanaiWidget';
import FeedbackFAB from '../modules/Feedback/FeedbackFAB';
import ForslagPanel from './forslag/ForslagPanel';
import oppnaForslag from './forslag/oppnaForslag';
import FeedbackHeaderBadge from '../modules/Feedback/FeedbackHeaderBadge';
import { SidebarNav } from './navigation/SidebarNav';
import { SettingsModal } from './navigation/SettingsModal';
import { GlobalSearch } from './navigation/GlobalSearch/GlobalSearch';
import { useFavorites } from '../hooks/useFavorites';
import { useTheme } from '../contexts/ThemeProvider';
import { ProjectContextProvider, useProjectContext } from '../contexts/ProjectContextProvider';
import { formatProjectLabel } from '../utils/projectLabel';
import { useBrodsmulaEtiketter } from '../utils/brodsmulaEtikett';
import TestProjektBadge, { arTestprojekt } from './TestProjektBadge';
import useModulstatistik from '../analytics/useModulstatistik';
import { webbVy } from '../analytics/resolveView';

// Basic breadcrumb builder based on path segments
function useBreadcrumbs() {
  const location = useLocation();
  const disableRecentModules = isRecentModulesDisabled();
  const segments = location.pathname.split("/").filter(Boolean);
  const crumbs = [];
  let pathAcc = "";
  segments.forEach((seg, idx) => {
    pathAcc += `/${seg}`;
    let label;
    let path = pathAcc;
    const lower = seg.toLowerCase();
    if (lower === 'dashboard') {
      label = 'Dashboard';
    } else if (lower === 'product-presentation') {
      label = 'Produktpresentation';
    } else if (lower === 'kalkylgenomgang') {
      label = 'Kalkylgenomgång';
    } else if (lower === 'calcreview') {
      label = 'Kalkylgenomgång';
    } else if (!disableRecentModules && lower === 'tenders') {
      label = 'Anbud';
      path = '/tenders';
    } else if (lower === 'contracts') {
      label = 'Avtal';
      path = '/contracts';
    } else if (lower === 'projects') {
      label = 'Projekt';
      path = '/projects';
    } else if (lower === 'purchasing') {
      label = 'Inköp';
      path = '/purchasing';
    } else if (lower === 'ai') {
      label = 'Kanai';
    } else if (lower === 'forms') {
      label = 'Formulär';
      path = '/forms';
    } else if (lower === 'orgv2') {
      label = 'Organisationsplan';
    } else if (lower === 'equippass') {
      label = 'Maskinregister';
    } else if (!isNaN(seg)) {
      label = `#${seg}`;
    } else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) {
      // Id i adressen visas inte; sidan kan sätta en riktig etikett via useBrodsmulaEtikett.
      label = 'Detaljer';
    } else if (segments[0] === 'admin' && idx > 0 && ADMIN_CRUMBS[lower]) {
      // Systemadmins vyer: svensk etikett ur vyregistret i stället för adressens ord. Session 10.
      label = ADMIN_CRUMBS[lower];
    } else {
      // Modulens namn ur registret (data/modules.js) i stället för adressens
      // råa ord ("bildbank", "hrm", "utlagg"). Fallback: ordet med stor
      // begynnelsebokstav och bindestreck som mellanslag. A15, 2026-10-01.
      const modul = MODULES.find((m) => m.to === pathAcc);
      if (modul?.label) {
        label = modul.label;
      } else {
        const ord = decodeURIComponent(seg).replace(/-/g, ' ');
        label = ord.charAt(0).toUpperCase() + ord.slice(1);
      }
    }

    crumbs.push({ label, path });
  });

  return crumbs.map((c, i) => ({ ...c, last: i === crumbs.length - 1 }));
}

// Breadcrumb + browser-tab title. Lever inne i ProjectContextProvider sa den kan
// byta ut det interna projekt-id:t (t.ex. "#12") mot "<nummer> <namn>" som anvandare
// faktiskt kanner igen (t.ex. "615002 TESTPROJEKT 1").
function BreadcrumbTrail({ isHome }) {
  const breadcrumbs = useBreadcrumbs();
  const { currentProject } = useProjectContext();
  const egnaEtiketter = useBrodsmulaEtiketter();

  const projectLabel = formatProjectLabel(currentProject);
  const enhanced = breadcrumbs.map((c) => {
    const m = c.path.match(/^\/projects\/(\d+)$/);
    if (m && projectLabel && currentProject && String(currentProject.id) === m[1]) {
      return { ...c, label: projectLabel, test: arTestprojekt(currentProject) };
    }
    if (egnaEtiketter[c.path]) return { ...c, label: egnaEtiketter[c.path] };
    return c;
  });

  // Dynamic browser tab title based on current page (project-aware).
  useEffect(() => {
    const egen = document.querySelector('[data-kp-title]')?.getAttribute('data-kp-title'); // sidan kan sätta egen titel
    if (egen) document.title = `${egen} — KanPlan`;
    else if (isHome || enhanced.length === 0) document.title = 'KanPlan';
    else document.title = `${enhanced[enhanced.length - 1].label} — KanPlan`;
  }, [enhanced, isHome]);

  if (isHome) return null;

  return (
    <nav aria-label="Breadcrumb" className="hb-breadcrumb">
      <Link to="/home/overview">Home</Link>
      {enhanced.map((c) => (
        <React.Fragment key={c.path}>
          <span className="hb-sep">›</span>
          {c.last ? (
            <span className="hb-current" aria-current="page">{c.label}</span>
          ) : (
            <Link to={c.path}>{c.label}</Link>
          )}
          {c.test && <TestProjektBadge className="hb-test" />}
        </React.Fragment>
      ))}
    </nav>
  );
}

function computeInitials(name, email) {
  const src = (name && String(name).trim()) || (email && String(email).trim()) || '';
  if (!src) return 'A';
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (src.includes('@')) return src[0].toUpperCase();
  return src.slice(0, 1).toUpperCase();
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [avatarBroken, setAvatarBroken] = useState(false);

  // Agent HQ avstangd 2026-08-12 (Mikaels beslut: tidigt sidospar). Anropet
  // useModuleTracking(user) postade telemetri till /api/agent-hq/module-visit
  // vid VARJE modulnavigering, for alla inloggade. Med ytan avstangd hade det
  // gett 403 i konsolen pa varje sidbyte, sa det ar borttaget harifran.

  useEffect(() => {
    setAvatarBroken(false);
  }, [user?.avatar_url, user?.id]);
  const { push } = useToast();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const contrastMenu = theme === 'contrast';
  const disableRecentModules = isRecentModulesDisabled();
  const [effectiveModuleKeys, setEffectiveModuleKeys] = useState(null);
  const [modulePermissions, setModulePermissions] = useState({});
  const [moduleIsAdmin, setModuleIsAdmin] = useState(false);
  const [noAccessModule, setNoAccessModule] = useState(null);
  const [tenantBrand, setTenantBrand] = useState({
    name: '',
    logoUrl: '',
    whitelabelEnabled: false,
  });
  const [tenantBrandReloadToken, setTenantBrandReloadToken] = useState(0);
  const [tenantModalOpen, setTenantModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return window.matchMedia?.('(min-width: 1024px)')?.matches ?? true;
    } catch {
      return true;
    }
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const { isFavorite: isFavoriteForSearch } = useFavorites();
  const kanaiWidgetRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const projectMatch = location.pathname.match(/^\/projects\/(\d+)/);

  // Allow other components to request opening Help via a global event
  useEffect(() => {
    const handler = () => setHelpOpen(true);
    window.addEventListener('open-help', handler);
    return () => window.removeEventListener('open-help', handler);
  }, []);

  // Auto-close sidebar on navigation change when on mobile (< 768px)
  useEffect(() => {
    const isMobile = window.matchMedia?.('(max-width: 767px)')?.matches;
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Mirror sidebarOpen to body class so navigation.css can show/hide the
  // sidebar (mobile: translateX, desktop: width collapse). useLayoutEffect
  // ensures the class is in sync before paint to avoid first-render flicker.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (sidebarOpen) document.body.classList.add('kp-sidebar-open');
    else document.body.classList.remove('kp-sidebar-open');
    return () => document.body.classList.remove('kp-sidebar-open');
  }, [sidebarOpen]);

  const currentProjectId = projectMatch ? projectMatch[1] : null;

  const effectiveModuleKeySet = useMemo(() => {
    if (!Array.isArray(effectiveModuleKeys)) return null;
    const s = new Set();
    for (const k of effectiveModuleKeys) {
      const key = String(k || '').trim();
      if (key) s.add(key);
    }
    return s;
  }, [effectiveModuleKeys]);

  // Snabb första rendering av menyn: senast kända modullista cachas per
  // användare + tenant. Utan cachen är sidomenyn reducerad tills BÅDA
  // menyanropen svarat — på en belastad server tog det ~10 s vid inloggning
  // (pilotfynd 2026-08-06). Cachen rensas vid utloggning (AuthProvider).
  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem('kp-modules-cache');
      if (!raw) return;
      const cached = JSON.parse(raw);
      const slug = String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim();
      const userKey = String(user?.id ?? user?.email ?? '');
      const simActive = Boolean(
        (localStorage.getItem('kanplan-simulated-role') || '').trim()
        || (localStorage.getItem('kanplan-simulated-user') || '').trim()
      );
      if (simActive) return; // cachen speglar inte simulering
      if (cached?.userKey === userKey && cached?.slug === slug && Array.isArray(cached?.modules)) {
        setEffectiveModuleKeys(cached.modules);
        setModulePermissions(cached.permissions || {});
        setModuleIsAdmin(Boolean(cached.isAdmin));
      }
    } catch { /* trasig cache ignoreras — servern är sanningskällan */ }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    async function loadTenantBrand() {
      if (!user) return;
      try {
        let tenantSlug = '';
        try {
          tenantSlug = String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim();
        } catch {}
        let simRole = '';
        let simUser = '';
        try {
          simRole = String(localStorage.getItem('kanplan-simulated-role') || '').trim();
          simUser = String(localStorage.getItem('kanplan-simulated-user') || '').trim();
        } catch {}

        const hdrs = {
          ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}),
          ...(simRole ? { 'X-Simulated-Role': simRole } : {}),
          ...(simUser ? { 'X-Simulated-User': simUser } : {}),
        };

        // Båda anropen avfyras PARALLELLT — tidigare kördes de seriellt vilket
        // dubblade väntetiden innan menyn kunde ritas komplett.
        const brandPromise = fetch(`${API_URL}/tenants/current`, {
          credentials: 'include',
          cache: 'no-store',
          headers: hdrs,
        }).catch(() => null);
        // RBAC-medveten modullista (respekterar X-Simulated-User/-Role) —
        // auktoritativ källa för menyfiltreringen.
        const modulesPromise = fetch(`${API_URL}/tenants/current/my-modules`, {
          credentials: 'include',
          cache: 'no-store',
          headers: hdrs,
        }).catch(() => null);

        let legacyEffective = null;
        const res = await brandPromise;
        if (res && res.ok) {
          const data = await res.json();
          const tenant = data?.tenant || {};
          const settings = tenant?.settings || {};
          if (!cancelled) {
            setTenantBrand({
              name: String(tenant?.name || ''),
              logoUrl: String(settings?.brand_logo_url || ''),
              whitelabelEnabled: Boolean(settings?.whitelabel_enabled),
            });
          }
          // Legacy-listan från /tenants/current respekterar INTE simulering.
          legacyEffective = data?.module_access?.effective;
        }

        let usedMyModules = false;
        try {
          const modRes = await modulesPromise;
          if (modRes && modRes.ok && !cancelled) {
            const modData = await modRes.json();
            if (simUser || simRole) {
              console.log('[KP-SIM] /my-modules response:', { simUser, simRole, is_admin: modData?.is_admin, moduleCount: modData?.modules?.length, modules: modData?.modules });
            }
            setModulePermissions(modData?.permissions || {});
            setModuleIsAdmin(Boolean(modData?.is_admin));
            if (Array.isArray(modData?.modules)) {
              setEffectiveModuleKeys(modData.modules);
              usedMyModules = true;
              // Spara för snabb meny vid nästa sidladdning (ej under simulering).
              if (!simRole && !simUser) {
                try {
                  localStorage.setItem('kp-modules-cache', JSON.stringify({
                    userKey: String(user?.id ?? user?.email ?? ''),
                    slug: tenantSlug,
                    modules: modData.modules,
                    permissions: modData?.permissions || {},
                    isAdmin: Boolean(modData?.is_admin),
                    savedAt: Date.now(),
                  }));
                } catch { /* localStorage full/avstängd — cachen är bara en optimering */ }
              }
            }
          }
        } catch {
          // Non-critical — fall through to legacy list below
        }

        // Fallback: use legacy module list only when /my-modules failed
        // and no simulation is active (legacy list ignores simulation).
        if (!usedMyModules && !cancelled) {
          if (!simRole && !simUser && Array.isArray(legacyEffective)) {
            setEffectiveModuleKeys(legacyEffective);
          }
          // Vid misslyckat anrop: behåll nuvarande värde (cache/simulering) —
          // nollställ inte menyn på grund av ett tillfälligt nätverksfel.
        }
      } catch {
        // ignore
      }
    }
    loadTenantBrand();
    return () => {
      cancelled = true;
    };
  }, [user, push, tenantBrandReloadToken]);

  // Allow admin UI to force-refresh tenant branding without full reload.
  useEffect(() => {
    const handler = (e) => {
      try {
        const wanted = String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim();
        const eventSlug = String(e?.detail?.slug || '').trim();
        if (eventSlug && wanted && eventSlug !== wanted) return;
      } catch {}
      setTenantBrandReloadToken((v) => v + 1);
    };
    window.addEventListener('kanplan-tenant-branding-updated', handler);
    return () => window.removeEventListener('kanplan-tenant-branding-updated', handler);
  }, []);

  const tenantLogoSrc = useMemo(() => {
    const raw = String(tenantBrand.logoUrl || '').trim();
    if (!raw) return '';
    // Data URLs shouldn't be cache-busted.
    if (raw.startsWith('data:image/')) return raw;
    // Bust browser cache for normal URLs whenever branding reloads.
    const sep = raw.includes('?') ? '&' : '?';
    return `${raw}${sep}v=${tenantBrandReloadToken}`;
  }, [tenantBrand.logoUrl, tenantBrandReloadToken]);

  useEffect(() => {
    if (!effectiveModuleKeySet) return;

    const pathname = String(location.pathname || '');
    const keyForPath = () => {
      const exact = {
        '/dashboard': 'dashboard',
        '/inbox': 'inbox',
        '/projects': 'projects',
        '/tenders': 'tenders',
        '/anbud-v2': 'tenders_v2',
        '/issues': 'issues',
        '/qa/controls': 'qa_controls',
        '/arbetsberedningar': 'arbetsberedning',
        '/ek-bibliotek': 'ek_bibliotek',
        '/contacts': 'contacts',
        '/contracts': 'contracts',
        '/bim': 'bim_viewer',
        '/resource-planning': 'resource_planning',
        '/revisions': 'revisions',
        '/training': 'onboarding',
        '/training/paths': 'onboarding',
        '/management-dashboard': 'management_dashboard',
        '/management-system': 'management_system',
        '/ice': 'management_system',
        '/finance-dashboard': 'finance_dashboard',
        '/product-presentation': 'product_presentation',
        '/insight-analytics': 'insight_analytics',
        '/rutinhantering': 'routine_management',
        '/purchasing': 'purchasing',
        '/fakturagranskning': 'invoice_review',
        '/kancalc': 'kancalc',
        '/project-map': 'project_map',
        '/insight': 'insight',
        '/kanai': 'kanai',
        '/knowledge': 'kanai',
        '/assessments': 'assessments',
        '/ia': 'ia_system',
        '/arbetsmiljodelegering': 'arbetsmiljodelegering',
        '/hrm': 'hrm',
        '/fleet': 'fleet',
        '/klimat': 'klimat',
        '/bak-avstamning': 'bak_avstamning',
        '/meetings': 'meetings',
        '/suppliers': 'supplier_api',
        '/crm': 'crm',
        '/orgplan': 'orgplan',
        '/contacts': 'contacts',
        '/open-books': 'open_books',
        '/time-reporting': 'time_reporting',
        '/eftermarknad': 'eftermarknad',
        '/payroll': 'payroll',
        '/hrm': 'hrm',
        // Utbildning och kompetens: samma krets som API:t (HR-insyn).
        '/hr/utbildning': 'hrm_kansliga',
        '/module-analytics': 'module_analytics',
        '/forms': 'forms',
        '/executive-dashboard': 'executive_dashboard',
        '/reports-360': 'reports_360',
        '/waste-analysis': 'waste_analysis',
      };
      if (exact[pathname]) return exact[pathname];
      // Prefix routes — projekt-nestade flikar gatas separat innan generell projects-fallback
      if (/^\/projects\/\d+\/inskrivning(\/|$)/.test(pathname)) return 'p.inskrivningar';
      if (pathname.startsWith('/projects/')) return 'projects';
      if (pathname.startsWith('/kancalc/')) return 'kancalc';
      if (pathname.startsWith('/training/paths/')) return 'onboarding';
      if (pathname.startsWith('/training/')) return 'onboarding';
      if (pathname.startsWith('/qa/')) return 'qa_controls';
      if (pathname.startsWith('/arbetsberedningar')) return 'arbetsberedning';
      if (pathname.startsWith('/ek-bibliotek')) return 'ek_bibliotek';
      if (pathname.startsWith('/bim/')) return 'bim_viewer';
      if (pathname.startsWith('/management-dashboard')) return 'management_dashboard';
      if (pathname.startsWith('/management-system/')) return 'management_system';
      if (pathname.startsWith('/finance-dashboard')) return 'finance_dashboard';
      if (pathname.startsWith('/product-presentation')) return 'product_presentation';
      if (pathname.startsWith('/insight-analytics')) return 'insight_analytics';
      if (pathname.startsWith('/rutinhantering')) return 'routine_management';
      if (pathname.startsWith('/ice')) return 'management_system';
      if (pathname.startsWith('/assessments')) return 'assessments';
      if (pathname.startsWith('/fleet')) return 'fleet';
      if (pathname.startsWith('/klimat')) return 'klimat';
      if (pathname.startsWith('/ia')) return 'ia_system';
      if (pathname.startsWith('/arbetsmiljodelegering')) return 'arbetsmiljodelegering';
      if (pathname.startsWith('/meetings')) return 'meetings';
      if (pathname.startsWith('/suppliers')) return 'supplier_api';
      if (pathname.startsWith('/crm')) return 'crm';
      if (pathname.startsWith('/open-books')) return 'open_books';
      if (pathname.startsWith('/time-reporting')) return 'time_reporting';
      if (pathname.startsWith('/payroll')) return 'payroll';
      if (pathname.startsWith('/hrm')) return 'hrm';
      if (pathname.startsWith('/cv-generator')) return 'cv_generator';
      if (pathname.startsWith('/reference-projects')) return 'reference_projects';
      if (pathname.startsWith('/signature-generator')) return 'signature_generator';
      if (pathname.startsWith('/module-analytics')) return 'module_analytics';
      if (pathname.startsWith('/orgplan')) return 'orgplan';
      if (pathname.startsWith('/contacts')) return 'contacts';
      if (pathname.startsWith('/revisions')) return 'revisions';
      if (pathname.startsWith('/resource-planning')) return 'resource_planning';
      if (pathname.startsWith('/purchasing')) return 'purchasing';
      if (pathname.startsWith('/fakturagranskning')) return 'invoice_review';
      if (pathname.startsWith('/contracts')) return 'contracts';
      if (pathname.startsWith('/eftermarknad')) return 'eftermarknad';
      if (pathname.startsWith('/waste-analysis')) return 'waste_analysis';
      if (pathname.startsWith('/portfoljrisk')) return 'portfoljrisk';
      if (pathname.startsWith('/insight')) return 'insight';
      if (pathname.startsWith('/tenders')) return 'tenders';
      if (pathname.startsWith('/anbud-v2')) return 'tenders_v2';
      if (pathname.startsWith('/issues')) return 'issues';
      return null;
    };

    // Handlistan ovan har underhallits manuellt och glidit ifran
    // modulkatalogen: 22 modulvyer lag i sidomenyn med nyckel men UTAN
    // ruttgrind, sa en anvandare utan nyckeln nadde dem via direkt URL
    // (bl.a. Fraga KanAI, Historikarkiv, Smartdok-arkiv, Energi, UE-lista,
    // Vaxel, Ledarskapsbedomning). Darfor kompletteras listan harifran med
    // MODULES — den enda sanningskallan for navigationen — sa att en ny
    // modul grindas automatiskt i stallet for att glommas.
    //
    // Handlistan gar FORST och lamnas oforandrad: den ar bredare an
    // MODULES-posternas exakta sokvagar (t.ex. /ia -> ia_system tacker alla
    // underrutter) och ska fortsatta vinna.
    //
    // Tva uteslutningar ar nodvandiga i derivationen:
    //   - Projektflik-nycklar (p.*) grindar FLIKAR inuti ett projekt, inte
    //     rutter. Posten { to: '/projects', moduleKey: 'p.inskrivningar' }
    //     hade annars grindat HELA projektomradet pa inskrivningsnyckeln.
    //   - Poster utan nyckel: de faller igenom till null som forut.
    const derivedKeyForPath = () => {
      const kandidater = MODULES
        .filter((m) => m.moduleKey && !m.moduleKey.startsWith('p.') && m.to !== '/projects')
        .map((m) => [String(m.to || ''), m.moduleKey])
        .filter(([to]) => to.length > 1)
        // Langst sokvag vinner, annars skulle en kort sokvag skugga en
        // langre under samma prefix (t.ex. /avvikelser och /avvikelser/forslag).
        // Avvikelser, Mal & nyckeltal och Lagbevakning har egna adresser och
        // nycklar sedan placering 2026-10; nycklarna kommer har ur MODULES.
        .sort((a, b) => b[0].length - a[0].length);
      const traff = kandidater.find(([to]) => pathname === to || pathname.startsWith(`${to}/`));
      return traff ? traff[1] : null;
    };

    const moduleKey = keyForPath() || derivedKeyForPath();
    if (!moduleKey) { setNoAccessModule(null); return; }
    if (!effectiveModuleKeySet.has(moduleKey)) {
      setNoAccessModule(moduleKey);
    } else {
      setNoAccessModule(null);
    }
  }, [effectiveModuleKeySet, location.pathname]);

  // Agent HQ avstangd 2026-08-12 (Mikaels beslut: tidigt sidospar som kan
  // stangas ner). Har lag en useEffect som vid VARJE modulnavigering
  // dispatchade ett kanplan-module-change-event och postade till
  // /api/agent-hq/module-visit — for alla inloggade, oavsett moduler. Det var
  // ocksa skalet att ytan inte kunde grindas: en routernyckel hade gett 403 i
  // konsolen pa varje sidbyte. Backend-ytan grindas nu pa legacy_avstangd.

  // ── Modulstatistik: mät vilken vy som används och hur länge ──
  // Vyn härleds ur modullistan (inte en handlista), projektflikar mäts via
  // adressens #flik och tiden räknas bara när fliken syns och personen är
  // aktiv. Se analytics/moduleTracker.js.
  useModulstatistik(webbVy(location.pathname, location.hash), 'web');

  // Bara plattformsrollen systemadmin. Legacy-rollen "admin" betyder tenant-admin
  // sedan 2026-08-06 och nekas av backend — menyn ska inte lova mer (beslut D1 2026-09-30).
  const showSystemAdminNav = user?.role === 'systemadmin' || user?.is_admin === true;

  const activeTenantRole = (() => {
    try { return (localStorage.getItem('kanplan-active-tenant-role') || '').trim(); } catch { return ''; }
  })();
  const showTenantAdminNav = showSystemAdminNav || ['tenant_admin', 'admin'].includes(String(activeTenantRole || '').toLowerCase());

  // When simulating a specific user, use their effective admin status for sidebar filtering
  const simUserActive = (() => {
    try { return (localStorage.getItem('kanplan-simulated-user') || '').trim(); } catch { return ''; }
  })();
  const simRoleActive = (() => {
    try { return (localStorage.getItem('kanplan-simulated-role') || '').trim(); } catch { return ''; }
  })();
  const isImpersonating = !!(simUserActive || simRoleActive);
  const showSystemAdminNavEffective = simUserActive ? moduleIsAdmin : showSystemAdminNav;
  const showTenantAdminNavEffective = simUserActive ? moduleIsAdmin : showTenantAdminNav;

  const activeTenantSlug = (() => {
    try { return (localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); } catch { return ''; }
  })();

  const activeTenantName = (() => {
    try { return (localStorage.getItem('kanplan-active-tenant-name') || '').trim(); } catch { return ''; }
  })();

  // --- Role simulation for systemadmins ---
  const [simulatedRole, setSimulatedRole] = useState(() => {
    try { return (localStorage.getItem('kanplan-simulated-role') || '').trim(); } catch { return ''; }
  });
  const [simulatedUser, setSimulatedUser] = useState(() => {
    try { return (localStorage.getItem('kanplan-simulated-user') || '').trim(); } catch { return ''; }
  });
  const [roleSimOpen, setRoleSimOpen] = useState(false);
  const [tenantRolesForSim, setTenantRolesForSim] = useState([]);
  const [tenantMembersForSim, setTenantMembersForSim] = useState([]);
  const [simUserSearch, setSimUserSearch] = useState('');
  const handleSimulatedRoleChange = (val) => {
    setSimulatedRole(val);
    setSimulatedUser('');
    setRoleSimOpen(false);
    try {
      if (val) localStorage.setItem('kanplan-simulated-role', val);
      else localStorage.removeItem('kanplan-simulated-role');
      localStorage.removeItem('kanplan-simulated-user');
    } catch { /* ignore */ }
    // Reload module access so sidebar reflects the simulated role
    setTenantBrandReloadToken((v) => v + 1);
  };

  const handleSimulatedUserChange = (userId) => {
    setSimulatedUser(userId);
    setSimulatedRole('');
    setRoleSimOpen(false);
    setSimUserSearch('');
    try {
      if (userId) localStorage.setItem('kanplan-simulated-user', userId);
      else localStorage.removeItem('kanplan-simulated-user');
      localStorage.removeItem('kanplan-simulated-role');
    } catch { /* ignore */ }
    setTenantBrandReloadToken((v) => v + 1);
  };

  // Load tenant RBAC roles for the simulation dropdown
  useEffect(() => {
    if (!showSystemAdminNav || !activeTenantSlug) {
      setTenantRolesForSim([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/tenants/current/roles`, {
          credentials: 'include',
          headers: { 'X-Tenant-Slug': activeTenantSlug },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [];
          setTenantRolesForSim(list);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [showSystemAdminNav, activeTenantSlug]);

  // Load tenant members for user-level simulation
  useEffect(() => {
    if (!showSystemAdminNav || !activeTenantSlug) {
      setTenantMembersForSim([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/tenants/current/members`, {
          credentials: 'include',
          headers: { 'X-Tenant-Slug': activeTenantSlug },
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          const list = Array.isArray(data?.items) ? data.items : [];
          setTenantMembersForSim(list);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [showSystemAdminNav, activeTenantSlug]);

  // Close role-sim dropdown on outside click
  useEffect(() => {
    if (!roleSimOpen) return;
    const handler = (e) => {
      const el = document.getElementById('hb-rolesim-dropdown');
      if (el && !el.contains(e.target)) setRoleSimOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [roleSimOpen]);

  const [menuSettingsOpen, setMenuSettingsOpen] = useState(false);

  useEffect(() => {
    if (!menuSettingsOpen) return;
    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuSettingsOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuSettingsOpen]);

  // Mirror the new theme system to <html> so Tailwind's `dark:` utilities
  // (configured with darkMode: 'class') still fire across the app. The
  // ThemeProvider applies `dark` to <body> for the new sidebar tokens — we
  // also want it on <html> so non-sidebar Tailwind chrome keeps switching.
  useEffect(() => {
    try {
      if (dark) {
        document.documentElement.classList.add('dark');
        document.documentElement.style.colorScheme = 'dark';
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.style.colorScheme = 'light';
      }
    } catch {}
  }, [dark]);

  const kanaiEnabled = !effectiveModuleKeySet || effectiveModuleKeySet.has('kanai');

  // Top-level nav items
  const navItems = useMemo(() => {
    const items = [
    { to: "/home/overview", label: "Start", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 10.5l8-6 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 9.5v9a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-9" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M10 20v-6h4v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/dashboard", label: "Dashboard", moduleKey: 'dashboard', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 3v18M3 12h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ) },
    { to: "/tenders", label: "Anbud", moduleKey: 'tenders', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 19V5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7 15l3-3 3 2 4-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/anbud-v2", label: "Anbud V2", moduleKey: 'tenders_v2', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="4" width="5" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
        <rect x="10" y="4" width="5" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
        <rect x="17" y="4" width="4" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ) },
    { to: "/inbox", label: "Inkorg", moduleKey: 'inbox', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M3 7h18v10H3z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 12h6l2 3h2l2-3h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/meetings", label: "Möten", moduleKey: 'meetings', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 9h8M8 13h6M8 17h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/issues", label: "Uppgifter", moduleKey: 'issues', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 9h8M8 13h6M8 17h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/qa/controls", label: "QA Kontrollplan", moduleKey: 'qa_controls', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M8 16h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M8 8h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/arbetsberedningar", label: "Arbetsberedningar", moduleKey: 'arbetsberedning', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M12 9v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M12 17h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/ek-bibliotek", label: "EK-biblioteket", moduleKey: 'ek_bibliotek', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/projects", label: "Projekt", moduleKey: 'projects', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/contacts", label: "Kontakter", moduleKey: 'contacts', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 20a7 7 0 0 1 18 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M20 6v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/orgplan", label: "Projektorganisation", moduleKey: 'orgplan', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <circle cx="12" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="5" cy="17" r="2.5" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="19" cy="17" r="2.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 8.5v3M12 11.5l-7 3M12 11.5l7 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/assessments", label: "Medarbetarsamtal", moduleKey: 'assessments', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M9 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="8" y="2" width="8" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/leadership-assessment", label: "Ledarskapsbedömning", moduleKey: 'leadership_assessment', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.27 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/bim", label: "BIM", moduleKey: 'bim_viewer', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M3 6.5l6-2 6 2 6-2v12l-6 2-6-2-6 2v-12z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M9 4.5v12" stroke="currentColor" strokeWidth="1.2" />
        <path d="M15 6.5v12" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ) },
    { to: "/contracts", label: "Avtal", moduleKey: 'contracts', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 12h8M8 16h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/suppliers", label: "Leverantörer", moduleKey: 'supplier_api', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M7 7h10l2 4v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-6l2-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7 11h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/crm", label: "CRM", moduleKey: 'crm', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 20a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M17 8l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/vaxel", label: "Växel", moduleKey: 'vaxel', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/resource-planning", label: "Resurser", moduleKey: 'resource_planning', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 20a7 7 0 0 1 18 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/revisions", label: "Revision", moduleKey: 'revisions', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 7h16v10H4z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 10h7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7 14h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/training", label: "Utbildning", moduleKey: 'onboarding', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 8h10M7 12h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M14.5 15l1.5 1.5L19 13.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/knowledge", label: "Knowledge", moduleKey: 'kanai', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 6.5c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v13c0-.6-.4-1-1-1H6c-1.1 0-2 .9-2 2V6.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M20 6.5c0-1.1-.9-2-2-2h-6c-1.1 0-2 .9-2 2v13c0-.6.4-1 1-1h7c1.1 0 2 .9 2 2V6.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/management-system", label: "Ledningssystem", moduleKey: 'management_system', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/management-dashboard", label: "Ledningsdashboard", moduleKey: 'management_dashboard', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 19V5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M4 19h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M8 16v-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M12 16v-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M16 16V8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/finance-dashboard", label: "Ekonomidashboard", moduleKey: 'finance_dashboard', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 9h18" stroke="currentColor" strokeWidth="1.6" />
        <path d="M9 9v12" stroke="currentColor" strokeWidth="1.6" />
        <path d="M13 13h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M13 16.5h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/insight-analytics", label: "Insight Analytics", moduleKey: 'insight_analytics', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M3 3v18h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 14l4-4 3 3 4-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="18" cy="7" r="1.5" fill="currentColor" />
      </svg>
    ) },
    { to: "/rutinhantering", label: "Rutinhantering", moduleKey: 'routine_management', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/purchasing", label: "Inköp", moduleKey: 'purchasing', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M6 6h15l-1.5 9H7.5L6 6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx="9" cy="19" r="1.5" fill="currentColor" />
        <circle cx="18" cy="19" r="1.5" fill="currentColor" />
      </svg>
    ) },
    { to: "/fakturagranskning", label: "Fakturagranskning", moduleKey: 'invoice_review', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M14 3v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="m9.5 13 5 5M14.5 13l-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/kancalc", label: "KanCalc", moduleKey: 'kancalc', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 8h6M7 12h8M7 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M6 5l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/calcreview", label: "Kalkylgranskning", moduleKey: 'calc_review', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/project-map", label: "Projektkarta", moduleKey: 'project_map', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M3 6.5l6-2 6 2 6-2v12l-6 2-6-2-6 2v-12z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M9 4.5v12" stroke="currentColor" strokeWidth="1.2" />
        <path d="M15 6.5v12" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    ) },
    { to: "/insight", label: "Regler & Nyheter", moduleKey: 'insight', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M7 8h10M7 12h8M7 16h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/open-books", label: "Open Books", moduleKey: 'open_books', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 7h6M9 11h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/time-reporting", label: "Tidsredovisning", moduleKey: 'time_reporting', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/payroll", label: "Lönehantering", moduleKey: 'payroll', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M9 7h6M9 11h6M9 15h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/hrm", label: "HR Management", moduleKey: 'hrm', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M19 8v6M16 11h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/cv-generator", label: "CV-Generator", moduleKey: 'cv_generator', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 18c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/modules", label: "Modulbibliotek", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ) },
    { to: "/reference-projects", label: "Referensprojekt", moduleKey: 'reference_projects', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 9h18M9 9v12" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="15" cy="15" r="2" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    ) },
    { to: "/signature-generator", label: "Signaturgenerator", moduleKey: 'signature_generator', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M22 7l-10 6L2 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/hms", label: "HMS", moduleKey: 'ia_system', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/skyddsronder", label: "Skyddsronder", moduleKey: 'ia_system', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/arbetsmiljodelegering", label: "Arbetsmiljödelegeringar", moduleKey: 'arbetsmiljodelegering', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="9" y="2" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <rect x="3" y="17" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <rect x="15" y="17" width="6" height="5" rx="1" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 7v5M6 17v-2a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/spatial", label: "Spatial AI", moduleKey: 'spatial', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M6 20h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/fleet", label: "Företagsbilar", moduleKey: 'fleet', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M5 17h14M5 17a2 2 0 01-2-2v-3a1 1 0 011-1h1l2-4h10l2 4h1a1 1 0 011 1v3a2 2 0 01-2 2M5 17a2 2 0 002 2h2a2 2 0 002-2M15 17a2 2 0 002 2h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="7.5" cy="17" r="1" fill="currentColor" />
        <circle cx="16.5" cy="17" r="1" fill="currentColor" />
      </svg>
    ) },
    { to: "/klimat", label: "Klimatberäkning", moduleKey: 'klimat', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 2v6l3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 2v6l-3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 8c2.5 2.5 2.5 6 0 8.5S11 19 8.5 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M7 8C4.5 10.5 4.5 14 7 16.5S13 19 15.5 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M12 8c0 4-3 7-6 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M12 8c0 4 3 7 6 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/waste-analysis", label: "Avfallsanalys", moduleKey: 'waste_analysis', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M3 6h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 6l1 14a2 2 0 002 2h8a2 2 0 002-2l1-14" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M10 11v5M14 11v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/acc", label: "ACC / BIM 360", moduleKey: 'acc', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/eftermarknad", label: "Eftermarknad", moduleKey: 'eftermarknad', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/rapportera", label: "Rapportera", icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
        <path d="M12 8v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="currentColor" strokeWidth="0.5" />
      </svg>
    ) },
    { to: "/ue-lista", label: "UE-lista", moduleKey: 'ue_lista', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m9 14 2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/foretagsinskrivning", label: "Företagsinskrivning", moduleKey: 'foretagsinskrivning', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M3 21h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M5 21V7l8-4v18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19 21V11l-6-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 9h.01M9 12h.01M9 15h.01M9 18h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/forms", label: "Formulär", moduleKey: 'forms', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M15 14l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    { to: "/executive-dashboard", label: "Executive Dashboard", moduleKey: 'executive_dashboard', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ) },
    { to: "/reports-360", label: "360 Rapporter", moduleKey: 'reports_360', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 2v6h6M8 13h8M8 17h8M8 9h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) },
    ];

    if (disableRecentModules) {
      return items.filter((it) => !['/tenders', '/anbud-v2', '/purchasing'].includes(it.to));
    }

    return items;
  }, [disableRecentModules]);

  const visibleNavItems = useMemo(() => {
    const result = navItems
      .filter((item) => !effectiveModuleKeySet || !item.moduleKey || effectiveModuleKeySet.has(item.moduleKey))
      .filter((item) => (!item.adminOnly && item.to !== '/admin') || showSystemAdminNavEffective)
      .filter((item) => !item.tenantAdminOnly || showTenantAdminNavEffective);
    if (effectiveModuleKeySet) {
      console.log('[KP-SIM] visibleNavItems:', result.length, '/', navItems.length, 'effectiveModuleKeySet size:', effectiveModuleKeySet.size, 'keys:', [...effectiveModuleKeySet]);
    }
    return result;
  }, [navItems, effectiveModuleKeySet, showSystemAdminNavEffective, showTenantAdminNavEffective]);

  const toPathname = (to) => (typeof to === 'string' ? to : (to?.pathname || ''));
  const toKey = (to) => {
    if (typeof to === 'string') return to;
    const p = to?.pathname || '';
    const s = to?.search || '';
    return `${p}${s}`;
  };
  const isActive = (to) => location.pathname === toPathname(to);

  // Skalets bakgrundspollning ligger i frontend/src/shell/hooks/ (A11 steg 1).
  const { unread, setUnread, refresh: refreshUnread } = useUnreadNotifications(user, push);
  const [notifOpen, setNotifOpen] = useState(false);
  // System health indicator (admin only)
  const sysHealthStatus = useSystemHealthQuick(showSystemAdminNav); // 'ok' | 'warning' | 'critical' | null
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifItems, setNotifItems] = useState([]);
  const notifRef = React.useRef(null);
  const bellRef = React.useRef(null);
  const [marking, setMarking] = useState(false);
  // Räknaren vid Inkorg i menyn (WS-prenumeration + polling)
  const inboxUnread = useInboxUnread(user, activeTenantSlug);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!notifOpen) return;
    function onDocClick(e) {
      const n = notifRef.current; const b = bellRef.current;
      if (n && n.contains(e.target)) return;
      if (b && b.contains(e.target)) return;
      setNotifOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [notifOpen]);

  async function loadNotifications() {
    setNotifLoading(true);
    try {
      const res = await fetch(`${API_URL}/notifications?limit=20`, { credentials: 'include' });
      if (res.ok) {
        const arr = await res.json();
        setNotifItems(Array.isArray(arr) ? arr : []);
      }
    } catch {}
    finally { setNotifLoading(false); }
  }

  async function markAllNotificationsRead() {
    if (!user || marking) return;
    setMarking(true);
    try {
      // get unread IDs first to be safe
      const listRes = await fetch(`${API_URL}/notifications?unread=true&limit=200`, { credentials: 'include' });
      let ids = [];
      if (listRes.ok) {
        const arr = await listRes.json();
        ids = (Array.isArray(arr) ? arr : []).map(n => n?.id).filter(Boolean);
      }
      if (ids.length > 0) {
        await fetch(`${API_URL}/notifications/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids })
        });
        push({ type: 'success', message: `Markerade ${ids.length} notifieringar som lästa` });
        // Update local list state
        setNotifItems(items => items.map(it => ids.includes(it.id) ? { ...it, is_read: true } : it));
      } else {
        push({ type: 'info', message: 'Inga olästa notifieringar' });
      }
      setUnread(0);
    } catch {}
    finally { setMarking(false); }
  }

  async function markOneRead(id) {
    try {
      await fetch(`${API_URL}/notifications/read`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ ids: [id] })
      });
      setNotifItems(items => items.map(it => it.id === id ? { ...it, is_read: true } : it));
      setUnread(u => Math.max(0, u - 1));
    } catch {}
  }

  function onBellClick() {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) {
      loadNotifications();
      // Räknaren pollas var 30:e sekund (A11); hämta direkt när klockan öppnas.
      try { refreshUnread?.(); } catch {}
    }
  }


  return (
    <ProjectContextProvider>
    <div className={`min-h-screen w-full flex bg-transparent text-slate-800 dark:text-slate-100 transition-colors`}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-brand text-white px-3 py-2 rounded-md">Skip to content</a>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — Global Navigation v2 (Fas 2c) */}
      <SidebarNav
        effectiveModuleKeySet={effectiveModuleKeySet}
        isSystemAdmin={showSystemAdminNavEffective}
        isTenantAdmin={showTenantAdminNavEffective}
        isImpersonating={isImpersonating}
        onOpenSettings={() => setMenuSettingsOpen(true)}
        unreadCounts={{ Inkorg: inboxUnread }}
        tenantBrand={tenantBrand}
        tenantLogoSrc={tenantLogoSrc}
        onLogoClick={() => navigate('/home/overview')}
      />

      {/* Settings modal — Fas 5 fullbyggd version (återställ favoriter,
          återställ collapsed-sektioner, tema-info). Tema växlas via
          dropdown i SidebarFooter. */}
      <SettingsModal open={menuSettingsOpen} onClose={() => setMenuSettingsOpen(false)} />
      {/* Main area */}
  <div className={`flex flex-col flex-1 min-w-0 md:pl-${0} ${''}`}> 
        {/* Wrapper – plain kp-bg background */}
  <div className="flex-1 flex flex-col" style={{ background: 'var(--kp-bg, #eceff5)' }}>
          <header className={`header-bar${contrastMenu ? ' header-bar--contrast' : ''}`}>
          {/* Backdrop behind header dropdowns (notifications/dev) */}
          {notifOpen && (
            <div
              className="fixed inset-0 z-[60] bg-black/10 dark:bg-black/30"
              aria-hidden="true"
              onClick={() => setNotifOpen(false)}
            />
          )}

          {/* ─── LEFT: sidebar toggle + logo + breadcrumbs ─── */}
          <div className="hb-left">
            <button
              className="hb-sidebar-toggle"
              aria-label={sidebarOpen ? 'Fäll ihop vänstermeny' : 'Visa vänstermeny'}
              aria-controls="primary-sidebar"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(o => !o)}
              title={sidebarOpen ? 'Fäll ihop vänstermeny' : 'Visa vänstermeny'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>

            {!sidebarOpen && (
              <div className="hb-search">
                <GlobalSearch
                  effectiveModuleKeySet={effectiveModuleKeySet}
                  isSystemAdmin={showSystemAdminNavEffective}
                  isTenantAdmin={showTenantAdminNavEffective}
                  isFavorite={isFavoriteForSearch}
                />
              </div>
            )}

            <BreadcrumbTrail isHome={isHome} />
          </div>

          {/* ─── RIGHT: tenant + bell + user + role + logout + help ─── */}
          <div className="hb-right">
            {activeTenantSlug && showTenantAdminNavEffective && (
              <div className="hb-tenant" onClick={() => setTenantModalOpen(true)} role="button" tabIndex={0} title="Byt företag" onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTenantModalOpen(true); } }}>
                <span className="hb-tenant-label">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                  </svg>
                  FÖRETAG
                </span>
                <span className="hb-tenant-value" title={activeTenantName || activeTenantSlug}>{activeTenantName || activeTenantSlug}</span>
                <button type="button" className="hb-tenant-switch" onClick={e => { e.stopPropagation(); setTenantModalOpen(true); }}>Byt</button>
              </div>
            )}

            <span className="hb-divider" />

            {/* ─── Notifications ─── */}
            {user && (
              <div className="relative" ref={notifRef}>
                <button ref={bellRef} type="button" onClick={onBellClick} disabled={marking} title="Notifieringar" className="hb-icon-btn">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {unread > 0 && (
                    <span className="hb-badge-count">{unread}</span>
                  )}
                </button>
                {notifOpen && (
                  <NotificationPanel
                    items={notifItems}
                    loading={notifLoading}
                    unreadCount={unread}
                    marking={marking}
                    onMarkAllRead={markAllNotificationsRead}
                    onMarkOneRead={markOneRead}
                    onOpenNotification={(n) => {
                      if (!n) return;
                      // Mark as read
                      if (!n.is_read) {
                        markOneRead(n.id);
                      }
                      // Navigate
                      try {
                        if (n.link) {
                          if (/^https?:\/\//i.test(n.link)) { window.open(n.link, '_blank'); }
                          else { navigate(n.link); }
                        }
                      } catch {}
                      setNotifOpen(false);
                    }}
                    onClose={() => setNotifOpen(false)}
                    onViewAll={() => { try { navigate('/inbox'); } catch {} setNotifOpen(false); }}
                  />
                )}
              </div>
            )}

            {/* ─── Feedback Pipeline badge (bara systemadmin: endpointen kräver
                 require_admin, och is_admin-konton fick 403 varje minut, A11) ─── */}
            {user?.role === 'systemadmin' && <FeedbackHeaderBadge />}

            {/* ─── System health indicator (admin only) ─── */}
            {showSystemAdminNavEffective && sysHealthStatus && (
              <Link
                to="/admin/system-health"
                className="hb-syshealth"
                title={`Systemhälsa: ${sysHealthStatus === 'ok' ? 'OK' : sysHealthStatus === 'warning' ? 'Varning' : 'Kritiskt'}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                  textDecoration: 'none', cursor: 'pointer', transition: 'opacity 0.15s',
                  color: sysHealthStatus === 'ok' ? '#16a34a' : sysHealthStatus === 'warning' ? '#d97706' : '#dc2626',
                  backgroundColor: sysHealthStatus === 'ok' ? '#f0fdf4' : sysHealthStatus === 'warning' ? '#fffbeb' : '#fef2f2',
                  border: `1px solid ${sysHealthStatus === 'ok' ? '#bbf7d0' : sysHealthStatus === 'warning' ? '#fde68a' : '#fecaca'}`,
                }}
              >
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  backgroundColor: sysHealthStatus === 'ok' ? '#16a34a' : sysHealthStatus === 'warning' ? '#d97706' : '#dc2626',
                  animation: sysHealthStatus !== 'ok' ? 'pulse 2s infinite' : 'none',
                }} />
                <span className="hb-syshealth-label">
                  {sysHealthStatus === 'ok' ? 'System OK' : sysHealthStatus === 'warning' ? 'Varning' : 'Kritiskt'}
                </span>
              </Link>
            )}

            <span className="hb-divider" />

            {/* ─── User block (clickable → /me) ─── */}
            {user && (
              <div className="hb-user-block" onClick={() => navigate('/me')} role="button" tabIndex={0} title="Min profil" onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/me'); } }}>
                <div className="hb-user-avatar">
                  {(user?.avatar_url && !avatarBroken) ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img
                      src={user.avatar_url}
                      alt="avatar"
                      onError={() => setAvatarBroken(true)}
                    />
                  ) : (
                    computeInitials(user?.name, user?.email)
                  )}
                </div>
                <div className="hb-user-info">
                  <span className="hb-user-name">{user.name}</span>
                  <span className="hb-user-role">{showSystemAdminNav ? 'Admin' : 'Medlem'}</span>
                </div>
              </div>
            )}

            {/* Profile + role sim + logout */}
            {user && (
              <>
                {showSystemAdminNav && (
                  <div id="hb-rolesim-dropdown" className="hb-rolesim">
                    <button
                      type="button"
                      className={`hb-role-pill${(simulatedRole || simulatedUser) ? ' hb-role-pill--active' : ''}`}
                      onClick={() => { setRoleSimOpen((v) => !v); setSimUserSearch(''); }}
                      title="Simulera roll eller medarbetare – testa behörighet"
                      aria-haspopup="listbox"
                      aria-expanded={roleSimOpen}
                    >
                      {/* Icon: shield for admin, person for simulated */}
                      {(!simulatedRole && !simulatedUser) ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="hb-role-pill-icon"><path d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="hb-role-pill-icon"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      )}
                      <span className="hb-role-pill-label">
                        {simulatedUser
                          ? (() => {
                              const m = tenantMembersForSim.find(mb => String(mb.user?.id) === simulatedUser);
                              return m ? m.user.name : 'Användare…';
                            })()
                          : !simulatedRole
                            ? 'Admin'
                            : simulatedRole === '_no_role'
                              ? 'Ingen roll'
                              : (() => {
                                  const r = tenantRolesForSim.find(tr => String(tr.id) === simulatedRole);
                                  return r ? r.name : 'Roll…';
                                })()
                        }
                      </span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="hb-role-pill-chevron"><path d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                    </button>

                    {roleSimOpen && (
                      <div className="hb-rolesim-menu" role="listbox" aria-label="Simulera roll eller medarbetare">
                        {/* Admin (default) */}
                        <button
                          type="button"
                          role="option"
                          aria-selected={!simulatedRole && !simulatedUser}
                          className={`hb-rolesim-opt${(!simulatedRole && !simulatedUser) ? ' hb-rolesim-opt--sel' : ''}`}
                          onClick={() => handleSimulatedRoleChange('')}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                          <span>Admin</span>
                          {(!simulatedRole && !simulatedUser) && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="hb-rolesim-check"><polyline points="20 6 9 17 4 12"/></svg>}
                        </button>

                        {/* Divider */}
                        <div className="hb-rolesim-divider" />

                        {/* No role (basic member) */}
                        <button
                          type="button"
                          role="option"
                          aria-selected={simulatedRole === '_no_role'}
                          className={`hb-rolesim-opt${simulatedRole === '_no_role' ? ' hb-rolesim-opt--sel' : ''}`}
                          onClick={() => handleSimulatedRoleChange('_no_role')}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          <span>Ingen roll</span>
                          {simulatedRole === '_no_role' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="hb-rolesim-check"><polyline points="20 6 9 17 4 12"/></svg>}
                        </button>

                        {/* Tenant RBAC roles */}
                        {tenantRolesForSim.length > 0 && (
                          <>
                            <div className="hb-rolesim-divider" />
                            {tenantRolesForSim.map(r => (
                              <button
                                key={String(r.id)}
                                type="button"
                                role="option"
                                aria-selected={simulatedRole === String(r.id)}
                                className={`hb-rolesim-opt${simulatedRole === String(r.id) ? ' hb-rolesim-opt--sel' : ''}`}
                                onClick={() => handleSimulatedRoleChange(String(r.id))}
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                <span>{r.name}{r.is_admin ? ' (Admin)' : ''}</span>
                                {simulatedRole === String(r.id) && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="hb-rolesim-check"><polyline points="20 6 9 17 4 12"/></svg>}
                              </button>
                            ))}
                          </>
                        )}

                        {/* Tenant members (user-level simulation) */}
                        {tenantMembersForSim.length > 0 && (
                          <>
                            <div className="hb-rolesim-divider" />
                            <div className="hb-rolesim-section-label">Medarbetare</div>
                            {tenantMembersForSim.length > 5 && (
                              <div style={{ padding: '2px 4px' }}>
                                <input
                                  type="text"
                                  className="hb-rolesim-search"
                                  placeholder="Sök medarbetare…"
                                  value={simUserSearch}
                                  onChange={e => setSimUserSearch(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  autoComplete="off"
                                />
                              </div>
                            )}
                            {tenantMembersForSim
                              .filter(m => {
                                if (!simUserSearch.trim()) return true;
                                const q = simUserSearch.toLowerCase();
                                return (m.user?.name || '').toLowerCase().includes(q)
                                    || (m.user?.email || '').toLowerCase().includes(q);
                              })
                              .slice(0, 20)
                              .map(m => {
                                const uid = String(m.user?.id);
                                const roleName = m.membership?.tenant_role_name || '';
                                return (
                                  <button
                                    key={uid}
                                    type="button"
                                    role="option"
                                    aria-selected={simulatedUser === uid}
                                    className={`hb-rolesim-opt${simulatedUser === uid ? ' hb-rolesim-opt--sel' : ''}`}
                                    onClick={() => handleSimulatedUserChange(uid)}
                                  >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                    <span style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                                      <span style={{ fontWeight: 500 }}>{m.user?.name || m.user?.email}</span>
                                      {roleName && <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>{roleName}</span>}
                                    </span>
                                    {simulatedUser === uid && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="hb-rolesim-check"><polyline points="20 6 9 17 4 12"/></svg>}
                                  </button>
                                );
                              })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <span className="hb-divider" />

                {/* aria-label + title kravs: under 1400px doljs texten "Logga ut"
                    (se HeaderBar.css), och utan namn last knappen bara upp som
                    "knapp" av skarmlasare. Samma monster som hb-role-pill. */}
                <button type="button" onClick={() => { glomInloggningsmal(); logout(); }} className="hb-logout" aria-label="Logga ut" title="Logga ut">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
                  <span>Logga ut</span>
                </button>
              </>
            )}

            <button
              type="button"
              onClick={()=> setHelpOpen(true)}
              className="hb-help-btn"
              aria-label="Hjälp"
              title="Hjälp"
            >
              <HelpCircle size={15} aria-hidden />
            </button>
          </div>
        </header>
          {/* Integration health alert-bar — visas bara om någon tenant-
              integration (Hogia, Fortnox, IA, ACC, Dalux …) är nere.
              Pollar /integrations/health/banner var 90s. */}
          <IntegrationHealthBanner />
          <main id="main-content" className={`relative flex-1 focus:outline-none ${location.pathname === '/training' || location.pathname === '/kanai' || location.pathname === '/historikarkiv' || location.pathname === '/project-map' || location.pathname.startsWith('/training/') ? 'p-0 overflow-hidden' : 'px-4 md:px-10 py-8'}`} role="main" tabIndex={-1}>
            <ModuleAccessContext.Provider value={{ modules: effectiveModuleKeySet, permissions: modulePermissions, isAdmin: moduleIsAdmin }}>
              {noAccessModule ? (() => {
                const navItem = navItems.find(n => n.moduleKey === noAccessModule) || {};
                const moduleLabel = navItem.label || noAccessModule;
                const handleRequestAccess = async () => {
                  const slug = (typeof localStorage !== 'undefined') ? localStorage.getItem('kanplan-active-tenant-slug') : '';
                  const res = await fetch(`${API_URL}/access-requests`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                      'Content-Type': 'application/json',
                      ...(slug ? { 'X-Tenant-Slug': slug } : {}),
                    },
                    body: JSON.stringify({ module_key: noAccessModule, module_label: moduleLabel }),
                  });
                  if (!res.ok) {
                    push({ type: 'error', message: 'Kunde inte skicka begäran. Försök igen.' });
                    throw new Error('request failed');
                  }
                  push({ type: 'success', message: 'Begäran skickad till administratör' });
                };
                return (
                  <NoAccessPage
                    module={moduleLabel}
                    onRequestAccess={handleRequestAccess}
                  />
                );
              })() : children}
            </ModuleAccessContext.Provider>
          </main>
        </div>
        <TenantSwitchModal open={tenantModalOpen} onClose={() => setTenantModalOpen(false)} />
        <HelpModal open={helpOpen} onClose={()=>setHelpOpen(false)} currentPath={location.pathname} currentHash={location.hash} />
        {/* En väg in (BL-169): sidfoten, hörnknappen och På gång öppnar samma panel. */}
        <ForslagPanel />
        <KanaiWidget ref={kanaiWidgetRef} visible={kanaiEnabled && location.pathname !== '/kanai' && !location.pathname.startsWith('/training')} />
        {(showSystemAdminNavEffective || (effectiveModuleKeys || []).includes('feedback_rapportera')) && (
          // På /training lyfts knappen över modulspelarens fasta bottenrad (Nästa).
          <FeedbackFAB basBotten={location.pathname.startsWith('/training') ? 80 : 24} />
        )}
        <SyncBanner />
        {/* ── Global footer (KanPlan design-system) ── */}
        <footer
          data-kp-global-footer
          className="relative z-20 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 flex items-center justify-between flex-wrap"
          style={{ minHeight: 48, padding: '8px 24px', fontFamily: "'Inter', sans-serif", gap: 8, display: location.pathname === '/historikarkiv' ? 'none' : undefined }}
        >
          {/* Left: logo-mark + brand + separator + copyright */}
          <div className="flex items-center" style={{ gap: 12 }}>
            <a href="/" className="flex items-center no-underline" style={{ gap: 7 }}>
              <span
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 20, height: 20, borderRadius: 5,
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                }}
              >
                <svg viewBox="0 0 12 12" fill="none" width={12} height={12}>
                  <path d="M2 5h3v5H2V5zm5-3h3v8H7V2z" fill="#fff" />
                </svg>
              </span>
              <span
                className="text-slate-900 dark:text-slate-200"
                style={{ fontSize: 13, fontWeight: 700, letterSpacing: '-0.02em' }}
              >
                KanPlan
              </span>
            </a>
            <span
              className="bg-slate-200 dark:bg-slate-700"
              style={{ width: 1, height: 16, flexShrink: 0 }}
            />
            <span
              className="text-slate-400 dark:text-slate-500"
              style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}
            >
              {(() => {
                const year = new Date().getFullYear();
                const owner = tenantBrand.whitelabelEnabled ? (tenantBrand.name || 'KanPlan') : 'KanPlan';
                return `© ${year} ${owner}`;
              })()}
            </span>
          </div>

          {/* Right: nav links + version (flexWrap: gruppen är ~370px — utan
              wrap skapade den sidleds-scroll på mobil, responsiv baslinje Track 0) */}
          <div className="flex items-center" style={{ gap: 6, flexWrap: 'wrap' }}>
            <button
              onClick={() => setHelpOpen(true)}
              className="text-slate-500 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-500/10"
              style={{
                fontSize: 11.5, fontWeight: 500, padding: '4px 10px',
                borderRadius: 6, border: 'none', background: 'none',
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s ease',
              }}
            >
              Hjälp
            </button>
            <button
              onClick={() => oppnaForslag({ kalla: 'sidfot' })}
              className="text-slate-500 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-500/10"
              style={{
                fontSize: 11.5, fontWeight: 500, padding: '4px 10px',
                borderRadius: 6, border: 'none', background: 'none',
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s ease',
              }}
            >
              Support
            </button>
            <Link
              to="/changelog"
              className="text-slate-500 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-500/10"
              style={{
                fontSize: 11.5, fontWeight: 500, padding: '4px 10px',
                borderRadius: 6, textDecoration: 'none', transition: 'all .15s ease',
              }}
            >
              Changelog
            </Link>
            <Link
              to="/roadmap"
              className="text-slate-500 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-500/10"
              style={{
                fontSize: 11.5, fontWeight: 500, padding: '4px 10px',
                borderRadius: 6, textDecoration: 'none', transition: 'all .15s ease',
              }}
            >
              Roadmap
            </Link>
            <Link
              to="/villkor"
              className="text-slate-500 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-500/10"
              style={{
                fontSize: 11.5, fontWeight: 500, padding: '4px 10px',
                borderRadius: 6, textDecoration: 'none', transition: 'all .15s ease',
              }}
            >
              Villkor
            </Link>
            <FooterVersionInfo />
          </div>
        </footer>
      </div>
    </div>
    </ProjectContextProvider>
  );
}

function FooterVersionInfo() {
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => {
    fetch('/version.json').then(r => r.ok ? r.json() : null).then(setInfo).catch(()=>{});
  }, []);
  const appV = (typeof process !== 'undefined' && process?.env?.REACT_APP_VERSION) || info?.app?.version || 'dev-local';
  const ch = (typeof process !== 'undefined' && process?.env?.REACT_APP_CHANNEL) || info?.app?.channel || 'dev';
  return (
    <span
      className="text-slate-400 dark:text-slate-500 flex items-center border-l border-slate-200 dark:border-slate-700"
      style={{ fontSize: 11, fontWeight: 500, gap: 6, marginLeft: 6, paddingLeft: 14, whiteSpace: 'nowrap' }}
    >
      <span
        className="bg-emerald-600 dark:bg-emerald-400"
        style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0 }}
      />
      {`v${appV} · ${ch}`}
    </span>
  );
}
